import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Layout, message } from 'antd';
import { connect } from 'react-redux';
import { Redirect, Route, withRouter } from 'react-router';
import { requestsForProposalRestCrudThunksForSupplier } from '../../thunks/requests_for_proposal_thunks';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import RouteWithSubRoutes from '../route_with_sub_routes/RouteWithSubRoutes';
import SubnavTabs from '../subnav_tabs/SubnavTabs';
import {
	isDatePastDue,
	requestForProposalStatusIndicatorFormatter,
	timeUntilOrSince,
} from '../../utils/DataFormatterUtils';
import moment from 'moment';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { generateBackLink } from '../../utils/HistoryUtils';
import { clearNavbarState, updateNavbarState } from '../../actions/navbar_actions';
import {
	BidderCumulativeStatus,
	getBidderCummulativeStatus,
	RFP_STATUS,
} from '../../constants/BidderStatuses';
import { readBidders } from '../../thunks/bidders_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import PastDue from '../work_order_row_display_simple/PastDue';

require('./SupplierRFPDetailPage.less');
const { Content } = Layout;

const SupplierRFPDetailPage: FC<any> = ({
	match,
	history,
	companyConfig,
	childRoutes,
	updateNavbar,
	location,
	clearNavbar,
	readAllBidders,
	getRequestForProposal,
}) => {
	const requestForProposalId = parseInt(match.params.id, 10);

	const [bidder, setBidder] = useState<any>();

	const [bidderFetching, setBidderFetching] = useState(true);

	useEffect(() => {
		getRequestForProposal(requestForProposalId);
	}, []);

	useEffect(() => {
		setBidderFetching(true);
		readAllBidders(requestForProposalId)
			.then((res) => {
				console.log(res);
				setBidder(nullSafeGet('0', res));
			})
			.catch(message.error)
			.finally(() => setBidderFetching(false));
	}, []);

	useEffect(() => {
		updateNavbar(
			'Back to RFPs',
			generateBackLink('/supplier/opportunities/overview/all', location)
		);

		return () => {
			clearNavbar();
		};
	}, []);

	const handleTabChange = useCallback(
		(key) => history.push(`/supplier/opportunities/detail/${requestForProposalId}/${key}`),
		[history, requestForProposalId]
	);

	const checkTabActive = useCallback(
		(key) => {
			const pathname = history.location.pathname;
			return pathname.indexOf(key) !== -1;
		},
		[history.location.pathname]
	);

	const tabs = useMemo(
		() => [
			{
				key: 'details',
				name: 'Overview',
			},
			{
				key: 'notes',
				name: 'Notes',
			},
		],
		[]
	);

	const requestForProposal = useMemo(() => nullSafeGet('requestForProposal', bidder), [bidder]);

	const cummulativeStatus = useMemo(() => getBidderCummulativeStatus(bidder), [bidder]);

	const statusHeader = useMemo(() => {
		if (nullSafeGet('status', requestForProposal) === RFP_STATUS.ACTIVE) {
			return requestForProposalStatusIndicatorFormatter(requestForProposal.status);
		} else {
			return cummulativeStatus;
		}
	}, [cummulativeStatus, requestForProposal]);

	const dueDate = useMemo(
		() =>
			nullSafeGet('dueDate', requestForProposal) ? (
				isDatePastDue(nullSafeGet('dueDate', requestForProposal)) ? (
					<PastDue
						dueDate={nullSafeGet('dueDate', requestForProposal)}
						showTime={false}
						showYear={false}
					/>
				) : (
					`Responses due ${timeUntilOrSince(requestForProposal.dueDate)}`
				)
			) : null,
		[requestForProposal]
	);

	const closedDate = useMemo(
		() =>
			nullSafeGet('publishedOrClosedOrAwardedAt', requestForProposal)
				? timeUntilOrSince(requestForProposal.publishedOrClosedOrAwardedAt)
				: null,
		[requestForProposal]
	);

	const headerSupportText = useMemo(() => {
		if (nullSafeGet('status', requestForProposal) === RFP_STATUS.ACTIVE) {
			return dueDate;
		} else {
			switch (cummulativeStatus) {
				case BidderCumulativeStatus.UnderReview:
				case BidderCumulativeStatus.Awarded:
				case BidderCumulativeStatus.Declined:
					return closedDate ? `Bidding closed ${closedDate}` : null;
				default:
					return null;
			}
		}
	}, [closedDate, cummulativeStatus, dueDate, requestForProposal]);

	return bidderFetching || !nullSafeGet('id', requestForProposal) ? (
		<div>
			<Route
				path="/supplier/opportunities/detail/:id"
				exact={true}
				render={(props) => (
					<Redirect
						to={{
							pathname: `/supplier/opportunities/detail/${requestForProposalId}/details`,
						}}
					/>
				)}
			/>
			{childRoutes.map((r, i) => (
				<RouteWithSubRoutes key={i} {...r} />
			))}
			<PageLoadingPlaceholder />
		</div>
	) : (
		<div>
			<Route
				path="/supplier/opportunities/detail/:id"
				exact={true}
				render={(props) => (
					<Redirect
						to={{
							pathname: `/supplier/opportunities/detail/${requestForProposalId}/details`,
						}}
					/>
				)}
			/>
			<Content className="rfpDetailPage" style={{ padding: '0 0.5em' }}>
				<div
					style={{
						margin: '0 -8px',
						padding: '24px',
						paddingBottom: 0,
						background: nullSafeGet('detail.config.theme.navblock', companyConfig),
						marginBottom: 8,
					}}
				>
					<div style={{ marginBottom: 24 }}>
						<h4 className="rfpDetailPage__title">
							{!requestForProposal.title ? '' : requestForProposal.title}
						</h4>
						<div className="rfpDetailPage__subtitleRow">
							<div className="rfpDetailPage__subtitle">{statusHeader}</div>
							{headerSupportText ? (
								<div className="rfpDetailPage__subtitle">{headerSupportText}</div>
							) : null}
						</div>
					</div>
					<SubnavTabs tabs={tabs} onChange={handleTabChange} isActive={checkTabActive} />
				</div>
				{childRoutes.map((r, i) => (
					<RouteWithSubRoutes key={i} {...r} />
				))}
			</Content>
		</div>
	);
};

const mapStateToProps = (state) => ({
	requestsForProposalFetching: state.requests_for_proposal.fetching,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config,
});

const mapDispatchToProps = (dispatch) => ({
	getRequestForProposal: (id) => dispatch(requestsForProposalRestCrudThunksForSupplier.readOne(id)),
	updateNavbar: (backlinkTitle, backlinkTo, navbarBackgroundColor) =>
		dispatch(updateNavbarState(backlinkTitle, backlinkTo, navbarBackgroundColor)),
	clearNavbar: () => dispatch(clearNavbarState()),
	readAllBidders: (rfpId) => dispatch(readBidders(ROLE_TYPES.SUPPLIER)(rfpId)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SupplierRFPDetailPage));
